import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input, OnChanges,
    Output, QueryList, SimpleChanges, ViewChild, ViewChildren
} from '@angular/core';
import {IDMap, KeyMap, ModelID, ModelName} from "../../_typing/generic-types";
import {ConstantProperty, isConstantProperty} from "../../_models/constant-property";
import {ComponentType} from "../../_models/component-type";
import {EventConfigColumn} from "../../_typing/config/config-column";
import {
    ColumnFormatsConfig,
    ColumnFormatsConfigDict, IColumnFormatsConfig,
    TableColumnMenuComponent
} from "../../forms/table-column-menu/table-column-menu.component";
import {
    CdkDrag,
    CdkDragDrop,
    CdkDragHandle,
    CdkDropList,
    moveItemInArray,
    transferArrayItem
} from "@angular/cdk/drag-drop";
import {BaseComponent} from "../../shared/base.component";
import {OptionListSearchComponent} from "../option-list-search/option-list-search.component";
import {DblClickCopyDirective} from "../../directives/dbl-click-copy.directive";
import {SharedPipesModule} from "../../shared/modules/shared-pipes.module";
import {SharedModule} from "../../shared/modules/shared-module.module";
import {DefaultValueFieldComponent} from "../default-value-field/default-value-field.component";
import {FilterByValuePipe} from "../../shared/pipes";
import {Observable, of, map, first} from "rxjs";
import {ConstantPropertyDataService} from "../../data/constant-property-data.service";
import {take, takeUntil} from "rxjs/operators";
import {ComponentTypeDataService} from "../../data/component-type-data.service";
import {TOOLTIP_SHOW_DELAY} from "../../shared/globals";

export interface EventColumnSelectorEmitter {
    available_columns: EventConfigColumn[];
    selected_columns: EventConfigColumn[];
    new_column_id: string | ModelID;
}

@Component({
    selector: 'event-column-selector',
    templateUrl: './event-column-selector.component.html',
    styleUrls: ['./event-column-selector.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        OptionListSearchComponent,
        CdkDropList,
        CdkDrag,
        DblClickCopyDirective,
        SharedModule,
        DefaultValueFieldComponent,
        TableColumnMenuComponent,
        CdkDragHandle,
        FilterByValuePipe
    ],
})
export class EventColumnSelectorComponent extends BaseComponent implements AfterViewInit, OnChanges {
    @Input() model_dict: KeyMap<ConstantProperty | ComponentType>;
    @Input() allowRelatedProperties: boolean = false;
    private _availableColumns: EventConfigColumn[];
    @Input() set availableColumns(availableColumns: EventConfigColumn[]) {
        this._availableColumns = availableColumns;
        this.availableLists = this.componentTypeAvailableLists?.toArray() || [];
        this.cdr.markForCheck();
    };

    get availableColumns(): EventConfigColumn[] {
        return this._availableColumns;
    }

    private _selectedColumns: EventConfigColumn[];
    @Input() set selectedColumns(selectedColumns: EventConfigColumn[]) {
        this._selectedColumns = selectedColumns || [];
        this._getSelectedRelatedProperties();
    };

    get selectedColumns(): EventConfigColumn[] {
        return this._selectedColumns;
    }

    @Input() formats_dict: ColumnFormatsConfigDict = {};
    @Input() allowDefaults: boolean = false;
    @Input() disabledByDefault: boolean = true;
    @Input() parentComponentType: ComponentType; //Used for toggle columns title and default values

    @Output() columns_changed = new EventEmitter<Partial<EventColumnSelectorEmitter>>();
    @Output() formats_dict_changed = new EventEmitter<ColumnFormatsConfigDict>();

    private _colMenuOptions = ['bold', 'italic', 'size', 'resize', 'fit_content', 'colour', 'background_colour', 'aggregations', 'decimals', 'align'];
    @Input() set colMenuOptions(options: string[]) {
        if (!options) {
            this._colMenuOptions = ['bold', 'italic', 'size', 'resize', 'fit_content', 'colour', 'background_colour', 'aggregations', 'decimals', 'align'];
        } else {
            this._colMenuOptions = options;
        }
        //this.changeDetectorRef.markForCheck();
    }

    get colMenuOptions(): string[] {
        return this._colMenuOptions;
    }

    private _relatedComponentTypes: ComponentType[];
    @Input() set relatedComponentTypes(relatedComponentTypes: ComponentType[]) {
        this._relatedComponentTypes = relatedComponentTypes;
        this.componentTypeDict = {};
        relatedComponentTypes?.forEach(rct => this.componentTypeDict[rct.id] = rct);
    };

    get relatedComponentTypes(): ComponentType[] {
        return this._relatedComponentTypes;
    }

    @ViewChild('selectedList', {static: true}) selectedList: CdkDropList;
    @ViewChildren('CTAvailableList') componentTypeAvailableLists: QueryList<CdkDropList>;
    @Output() dropListsChanged = new EventEmitter<void>();

    // Combine default and component lists for selectedList's connections
    getAvailableLists(): CdkDropList[] {
        return this.componentTypeAvailableLists?.toArray() || [];
    }

    availableLists: CdkDropList[] = [];
    componentTypeColumns: EventConfigColumn[];
    default_format_values: Partial<IColumnFormatsConfig> = {resize: true};
    availableColumnFilterValue: string = '';
    selectedColumnFilterValue: string = '';
    expandedColumnOptions: IDMap<boolean> = {'availableList': true};
    componentTypePropertyDict: IDMap<EventConfigColumn[]> = {};
    filteredCTPropertyDict: IDMap<EventConfigColumn[]> = {};
    componentTypeDict: IDMap<ComponentType> = {};
    propertiesLoadingDict: IDMap<boolean> = {};
    attributes = ['changed_by', 'start_time', 'end_time'];
    private inputComplete: boolean = false;

    constructor(private cdr: ChangeDetectorRef,
                private propertyData: ConstantPropertyDataService,
                private componentTypeData: ComponentTypeDataService) {
        super();
    }

    ngAfterViewInit(): void {
        // Initialize availableLists after view children are available
        this.availableLists = this.getAvailableLists();
        this.cdr.detectChanges();

        // Subscribe to changes in ViewChildren to keep availableLists in sync
        this.componentTypeAvailableLists.changes
            .pipe(takeUntil(this.onDestroy))
            .subscribe(() => {
                this.availableLists = this.getAvailableLists();
                this.cdr.markForCheck();
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes['parentComponentType'];
        if (change && !change.firstChange && change.previousValue?.id !== change.currentValue?.id) {
            this.inputComplete = false;
        }
        const hasColumns = Array.isArray(this._availableColumns);
        const hasTypes = !!this._relatedComponentTypes?.length;
        const selectedSet = !!this._selectedColumns;
        if (hasColumns && hasTypes && selectedSet && !this.inputComplete) {
            this.updateComponentTypeColumns();
            this.inputComplete = true;
        }
        // Always update availableLists
        this.availableLists = this.componentTypeAvailableLists?.toArray() || [];
    }

    private updateComponentTypeColumns(): void {
        const allColumns = this.selectedColumns.concat(this._availableColumns);
        this.componentTypeColumns = allColumns.filter(col => col.type === 'component_type');
        this._availableColumns = this._availableColumns.filter(col => col.type !== 'component_type');
        this.cdr.markForCheck();
    }

    getColumnDataType(column: EventConfigColumn): string {
        // Handle different column types
        if (column.type === 'attribute') {
            // Attributes like start_time, end_time are datetime, others are string
            if (column.id === 'start_time' || column.id === 'end_time') {
                return 'datetime';
            }
            return 'string';
        } else if (column.type === 'constant_property') {
            // For linked properties with compound IDs (typeId:propertyId), extract the property ID
            let propertyId = column.id;
            if (column.related_type_id && typeof column.id === 'string' && column.id.includes(':')) {
                const parts = column.id.split(':');
                if (parts.length === 2) {
                    propertyId = parts[1]; // Extract property ID from compound ID
                }
            }

            // For constant properties, get the data type from the model_dict
            const property = this.model_dict?.[propertyId] as ConstantProperty;
            if (property?.attributes?.data_type) {
                return property.attributes.data_type;
            }
        } else if (column.type === 'component_type') {
            // Component type columns are essentially string (component names)
            return 'string';
        }
        // Default to string if unknown
        return 'string';
    }

    moveColumns(move: 'select' | 'remove'): void {
        this.availableLists = this.getAvailableLists();
        if (move === 'select') {
            let allAvailable = this.relatedComponentTypes?.length ? this.relatedComponentTypes.reduce((acc, componentType) => {
                return acc.concat(this.filteredCTPropertyDict[componentType.id] ?? [])
            }, this.availableColumns) : this.availableColumns;

            this._selectedColumns = this.selectedColumns.concat(allAvailable.map(col => {
                col.disabled = this.disabledByDefault;
                return col;
            }));
            this._availableColumns = [];
        } else {
            this._availableColumns = this._availableColumns.concat(this.selectedColumns.filter(col => {
                if (!this.relatedComponentTypes?.length) {
                    return col;
                }
                return col.type !== 'component_type' && !col.related_type
            }));
            this._selectedColumns = [];
        }
        if (this.relatedComponentTypes?.length) {
            this.relatedComponentTypes.forEach(ct => this._setFilteredComponentTypeData(ct.id));
        }
        this.columns_changed.emit({available_columns: this.availableColumns, selected_columns: this.selectedColumns});
        this.cdr.markForCheck();
    }

    moveColumn(move: 'select' | 'remove', column: EventConfigColumn, containerId?: string, droppedIndex?: number) {
        this.availableLists = this.getAvailableLists();
        if (move === 'select') {
            const availableList = this.availableLists?.find(list => list.id === containerId);
            const previousIndex = availableList.data.indexOf(column);
            const index = droppedIndex ?? this.selectedColumns.length;
            column.disabled = this.disabledByDefault;

            transferArrayItem(
                availableList.data,
                this.selectedColumns,
                previousIndex,
                index);
        } else {
            /**Since selectList is linked to many dynamically created available lists, make sure that items are returned to the correct list**/
            // Filter by both id and related_type_id to avoid removing columns with the same property but different relationships
            this._selectedColumns = this.selectedColumns.filter(s =>
                s.id !== column.id || s.related_type_id !== column.related_type_id
            );
            const actualListId = column.related_type_id ?? 'availableList';
            if (actualListId === 'availableList' && (column.type !== 'component_type' || !this.relatedComponentTypes?.length)) {
                // Fix: Insert at the correct index instead of pushing to end
                const targetIndex = droppedIndex ?? this._availableColumns.length;
                this._availableColumns.splice(targetIndex, 0, column);
            } else {
                this._setFilteredComponentTypeData(column.type === 'component_type' ? column.id : actualListId);
            }
        }

        this._emitChanges(column.id);
        this.cdr.markForCheck();
    }

    drop(event: CdkDragDrop<EventConfigColumn[]>): void {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
            this._emitChanges();
        } else {
            const move = event.previousContainer.id === 'selectedList' ? 'remove' : 'select';
            const item = event.item.data;
            this.moveColumn(move, item, event.previousContainer.id, event.currentIndex);
        }
    }

    private _emitChanges(addedColumnId?: string) {
        // update objects reference so that filter pipe is called
        this._availableColumns = Object.assign([], this.availableColumns);
        this._selectedColumns = Object.assign([], this.selectedColumns);

        this.columns_changed.emit({
            available_columns: this.availableColumns,
            selected_columns: this.selectedColumns,
            new_column_id: addedColumnId
        });
    }

    formatsChanged(event) {
        this.formats_dict_changed.emit(event);
        this.cdr.markForCheck();
    }

    columnFilter(item, value, index): boolean {
        // store item index for use in determine actual position in the items list
        item.index = index;
        if (item.type === 'attribute' || !this.model_dict?.[item.id]) {
            return item.id.toLowerCase().includes(value.toLowerCase()) || item.title?.toLowerCase().includes(value.toLowerCase());
        }
        if (this.model_dict?.[item.id]) {
            return this.model_dict[item.id].attributes.name.toLowerCase().includes(value.toLowerCase());
        }
        this.cdr.markForCheck();
    }

    toggleColumnOptions(componentTypeId: ModelID) {
        this.expandedColumnOptions[componentTypeId] = !this.expandedColumnOptions[componentTypeId];

        if (this.expandedColumnOptions[componentTypeId] && componentTypeId !== 'availableList') {
            this.getComponentTypeProperties(componentTypeId).pipe(take(1)).subscribe(() => {
                this._setFilteredComponentTypeData(componentTypeId);
            });
        }
        this.cdr.markForCheck();
    }

    private _setFilteredComponentTypeData(componentTypeId: ModelID): void {
        /**Link column is the column of type component_type that exists in the standard available list, but moved to the
         * component type's group here as it makes better sense visually**/
        const linkCol = this.componentTypeColumns.find(col => col.id === componentTypeId);
        const linkIsSelected = this.selectedColumns.findIndex(col => col.id === componentTypeId) > -1;
        let selectedPropertyIds = this.selectedColumns.filter(col => col.related_type_id === componentTypeId)?.map(col => col.id);
        this.filteredCTPropertyDict[componentTypeId] = (linkIsSelected ? [] : (linkCol ? [linkCol] : []))
            .concat(this.componentTypePropertyDict[componentTypeId]?.filter(col => {
                return col && selectedPropertyIds.indexOf(col.id) < 0;
            }));
        this.availableLists = this.getAvailableLists();
        this.cdr.markForCheck();
        this.dropListsChanged.emit();
    }

    /**This is called only when related type available column list is toggled open, only loads once per form load**/
    getComponentTypeProperties(componentTypeId: ModelID): Observable<EventConfigColumn[]> {
        if (this.relatedComponentTypes?.length && !this.allowRelatedProperties && !this.componentTypePropertyDict?.[componentTypeId]) {
            this.componentTypePropertyDict[componentTypeId] = [];
        }
        if (this.componentTypePropertyDict[componentTypeId] || this.propertiesLoadingDict[componentTypeId]) {
            return of(this.componentTypePropertyDict[componentTypeId]);
        }
        const componentType = this.componentTypeDict?.[componentTypeId];
        this.propertiesLoadingDict[componentTypeId] = true;
        return this.propertyData.getPropertiesByRelationshipId(componentTypeId, 'component_types')
            .pipe(map(response => {
                this.componentTypePropertyDict[componentTypeId] = response.data ? response.data.map(cp => {
                    this.model_dict[cp.id] = cp;
                    return new EventConfigColumn(cp, 'constant_property', componentType)
                }) : [];
                this.componentTypePropertyDict[componentTypeId] = this.componentTypePropertyDict[componentTypeId].concat(
                    this.attributes.map(a => new EventConfigColumn(a, 'attribute', componentType))
                )

                this.propertiesLoadingDict[componentTypeId] = false;
                return this.componentTypePropertyDict[componentTypeId];
            }));
    }

    getConstantProperty(columnId: string): ConstantProperty | undefined {
        const value = this.model_dict[columnId];
        return value as ConstantProperty;
    }

    getComponentType(columnId: string): ComponentType | undefined {
        const value = this.model_dict[columnId];
        return value as ComponentType;
    }

    /**Make sure that properties and component types for selected columns are at least loaded**/
    private _getSelectedRelatedProperties() {
        const relatedSelectedPropIds = this.selectedColumns.filter(col => col.related_type_id && col.type === 'constant_property')?.map(col => col.id);
        if (!relatedSelectedPropIds.length) return;
        this.propertyData.getConstantProperties(relatedSelectedPropIds).pipe(first(), takeUntil(this.onDestroy))
            .subscribe(result => result.data.forEach(cp => {
                this.model_dict[cp.id] = cp;
                this.cdr.markForCheck();
            }))
    }

    fitHeight(columnId: string): boolean {
        const cp = this.model_dict[columnId];
        if (!isConstantProperty(cp) || !cp?.attributes?.data_type) {
            return false;
        }
       // console.log("allow",!!['user','account'].includes(cp.attributes.data_type) && this.allowDefaults);
        return !!['user','account'].includes(cp.attributes.data_type) && this.allowDefaults;
    }

    protected readonly isConstantProperty = isConstantProperty;
    protected readonly TOOLTIP_SHOW_DELAY = TOOLTIP_SHOW_DELAY;
}
